import { defineStore } from 'pinia';
import { ref } from 'vue';
import type { Sale } from '../types';
import { saleApi } from '../services/api';

export const useSaleStore = defineStore('sales', () => {
  const sales = ref<Sale[]>([]);
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  async function fetchSales() {
    isLoading.value = true;
    error.value = null;
    try {
      sales.value = await saleApi.getSales();
    } catch (e) {
      error.value = 'Failed to fetch sales.';
    } finally {
      isLoading.value = false;
    }
  }

  async function addSale(sale: Omit<Sale, 'id'>) {
    isLoading.value = true;
    error.value = null;
    try {
      const newSale = await saleApi.addSale(sale);
      sales.value.push(newSale);
    } catch (e) {
      error.value = 'Failed to add sale.';
    } finally {
      isLoading.value = false;
    }
  }

  async function updateSale(updatedSale: Sale) {
    isLoading.value = true;
    error.value = null;
    try {
      const returnedSale = await saleApi.updateSale(updatedSale);
      const index = sales.value.findIndex(s => s.id === returnedSale.id);
      if (index !== -1) {
        sales.value[index] = returnedSale;
      }
    } catch (e) {
      error.value = 'Failed to update sale.';
    } finally {
      isLoading.value = false;
    }
  }

  async function deleteSale(id: string) {
    isLoading.value = true;
    error.value = null;
    try {
      await saleApi.deleteSale(id);
      sales.value = sales.value.filter(s => s.id !== id);
    } catch (e) {
      error.value = 'Failed to delete sale.';
    } finally {
      isLoading.value = false;
    }
  }

  function getSale(saleId: string) {
    return sales.value.find(s => s.id === saleId);
  }

  return {
    sales,
    isLoading,
    error,
    fetchSales,
    addSale,
    getSale,
    updateSale,
    deleteSale
  };
});
